import { Component, OnInit } from '@angular/core';
import { Observable, catchError, forkJoin, mergeMap, of } from 'rxjs';

import { ChatMessengerService } from '../chat-messenger.service';
import { CannedLibraryService, GetCannedLibraryMessageRequest, SmsCannedLibrary, SmsCannedLibraryMessage } from '../../../lib'
import { ToastService } from '../../toast/toast.service';

export interface LibraryResponse {
  library: SmsCannedLibrary;
  responses: SmsCannedLibraryMessage[];
}

export interface CannedResponse {
  libraryName: string;
  response: SmsCannedLibraryMessage;
}

@Component({
  selector: 'app-chat-responses',
  templateUrl: './chat-responses.component.html',
  styleUrls: ['./chat-responses.component.scss']
})
export class ChatResponsesComponent implements OnInit {
  isLoading: boolean = true;
  libraryResponses: LibraryResponse[] = [];
  cannedResponses: CannedResponse[] = [];
  selectedLibraryIndex: number = -1;
  selectedResponseIndex: number = -1;
  syStudentId: number | undefined;
  searchString: string = '';

  constructor(
    private messenger: ChatMessengerService,
    private libraryService: CannedLibraryService,
    private toastService : ToastService
  ) { }

  ngOnInit(): void {
    this.messenger.$activeSyStudentId.subscribe(syStudentId => {
      this.syStudentId = syStudentId;
      this.getLibrariesAndResponses();
      this.backToLibrary()
    })
  }

  getLibrariesAndResponses(): void {
    this.libraryResponses = [];
    this.isLoading = true;

    this.libraryService.apiSmsCannedLibraryPost({}).pipe(
      catchError(error => {
        console.error(`apiSmsCannedLibraryPost error: ${JSON.stringify(error)}`);
        this.toastService.show({ body: 'Server response error on getting Canned Library', classname: 'bg-danger text-light', delay: 5000, header: 'SMS Canned Library Failed' });
        return [];
      }),
      mergeMap(libraries => {
        let response$: Observable<SmsCannedLibraryMessage[]>[] = [];
        libraries.forEach(library => {
          if (library.id !== null && library.id !== undefined) {
            const req: GetCannedLibraryMessageRequest = {
              libraryId: library.id,
              syStudentId: this.syStudentId
            };

            response$.push(this.libraryService.apiSmsCannedLibraryMessagePost(req));
          }
        });

        return forkJoin([of(libraries), forkJoin(response$)]);
      })
    ).subscribe(([libraries, responses]) => {
      libraries.forEach((library, index) => {
        this.libraryResponses.push({ library: library, responses: responses[index] });
      });

      this.isLoading = false;
    });
  }

  librarySelected(index: number): void {
    this.selectedLibraryIndex = index;

    const libraryID = this.libraryResponses[index].library.id;
    if (libraryID) {
      this.cannedResponses = this.libraryResponses[index].responses.map(response => {
        const cannedResponse: CannedResponse = {
          libraryName: this.libraryResponses[index].library.name ?? '',
          response: response
        };
        return cannedResponse;
      });
    }
  }

  backToLibrary(): void {
    this.searchString = '';
    this.cannedResponses = [];
    this.selectedLibraryIndex = -1;
  }

  responseSelected(index: number): void {
    if (this.selectedResponseIndex === index) {
      this.selectedResponseIndex = -1;
    } else {
      this.selectedResponseIndex = index;
    }
  }

  applyResponse(index: number): void {
    const msg = this.cannedResponses?.[index].response.texts?.[0].content;
    if (msg) {
      this.messenger.setResponseText(msg);
    }
  }

  checkForEnterKey(event: KeyboardEvent): boolean {
    if (event.key === 'Enter') {
      if (this.searchString.length > 0) {
        this.search();
      }

      return false;
    } else {
      return true;
    }
  }

  search(): void {
    const lowerCaseSearchString = this.searchString.toLowerCase();
    this.cannedResponses = [];

    this.libraryResponses.forEach(libraryResponse => {
      const filteredResponses = libraryResponse.responses.filter(response =>
        response.name && response.name.toLowerCase().includes(lowerCaseSearchString)
      );

      if (filteredResponses.length > 0) {
        this.cannedResponses = this.cannedResponses.concat(filteredResponses.map(response => {
          const cannedResponse: CannedResponse = {
            libraryName: libraryResponse.library.name ?? '',
            response: response
          };
          return cannedResponse;
        }));
      }
    });

    this.selectedLibraryIndex = -2;
  }

}
